import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeroImage } from '@guryou/html-components';
import { stateIsLoaded, stateIsLoading, useVideoCourse } from '@guryou/redux';
import { Icon, Pagination, SkeletonLoader } from '../../components/shared';
import VideoCourseCard from './VideoCourseCard';

const PublicVideoCoursesLanding = () => {
  const { t } = useTranslation();
  const videoCourses = useVideoCourse();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');

  const allVideoCourses = (stateIsLoaded(videoCourses) && videoCourses?.data?.length && videoCourses.data) || [];

  const filteredVideoCourses = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) {
      return allVideoCourses;
    }
    return allVideoCourses.filter(course => {
      const name = String(course?.name || '').toLowerCase();
      const providerName = String(course?.providerName || '').toLowerCase();
      const description = String(course?.description || '')
        .replace(/<[^>]*>/g, ' ')
        .toLowerCase();
      return name.includes(q) || providerName.includes(q) || description.includes(q);
    });
  }, [allVideoCourses, searchTerm]);

  const totalItems = filteredVideoCourses.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVideoCourses = filteredVideoCourses.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <main>
      <section className="relative flex flex-col items-center justify-center bg-cover bg-no-repeat bg-center min-h-[480px] sm:min-h-[560px] pt-28 md:pt-32">
        <div className="absolute inset-0 brightness-50 grayscale bg-cover bg-center -z-10" style={{ backgroundImage: `url(${HeroImage})` }} />
        <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-12 lg:px-6 w-full">
          <div className="max-w-screen-md mx-auto text-center">
            <h1 className="mb-4 text-4xl tracking-tight font-extrabold text-white">{t('VIDEO_COURSE_PAGE.stay')}</h1>
            <p className="mb-8 font-light text-gray-100 sm:text-xl">{t('VIDEO_COURSE_PAGE.forBest')}</p>
            <div className="relative max-w-xl mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              <input
                type="search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('GENERAL.search', 'Cerca')}
                className="block w-full p-4 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-feel-primary focus:border-feel-primary"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="py-12 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6">
          {stateIsLoading(videoCourses) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <SkeletonLoader type="compactCard" count={6} />
            </div>
          ) : paginatedVideoCourses.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {paginatedVideoCourses.map((course, index) => (
                  <VideoCourseCard course={course} key={course?.id ?? index} isEnrolled={false} />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onPreviousPage={handlePreviousPage}
                  onNextPage={handleNextPage}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  totalItems={totalItems}
                />
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center p-4 mb-6 rounded-full bg-feel-primary/10">
                <Icon name="play" size="lg" className="text-feel-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('VIDEO_COURSE_PAGE.noVideoCourses')}</h2>
              <p className="text-gray-600 max-w-md mx-auto">{t('VIDEO_COURSE_PAGE.noVideoCoursesDescription')}</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default PublicVideoCoursesLanding;
